// ========================================
// ゲーム定数
// ========================================

import type { CardAttribute, CardType } from '../types';

export const INITIAL_LIFE = 6000;
export const INITIAL_SP = 1;
export const MAX_SP = 10;
export const INITIAL_HAND_SIZE = 5;
export const DECK_SIZE = 40;
export const COUNTER_ATTACK_RATE = 0.3;

// ========================================
// バランス調整ルール（false で無効化可能）
// ========================================

// 【孤軍奮闘】自分のモンスターが相手より少ない場合、差に応じて補正
// 1体差: ATK+10%、被ダメ-10%
// 2体差: ATK+25%、被ダメ-20%
// 3体以上: ATK+40%、被ダメ-30%
export const RULE_LONE_WARRIOR = true;

// 【貫通ダメージ】モンスターを破壊した時、余剰ダメージの50%を相手ライフに与える
export const RULE_PIERCING_DAMAGE = true;
export const PIERCING_DAMAGE_RATE = 0.5; // 貫通ダメージ率

// 孤軍奮闘の補正値テーブル
export interface LoneWarriorBonusEntry {
  atkBonus: number;
  damageReduction: number;
}

export const LONE_WARRIOR_BONUS: Record<number, LoneWarriorBonusEntry> = {
  1: { atkBonus: 0.10, damageReduction: 0.10 }, // 1体差
  2: { atkBonus: 0.25, damageReduction: 0.20 }, // 2体差
  3: { atkBonus: 0.40, damageReduction: 0.30 }, // 3体以上差
};

export const PHASES = ['ターン開始', 'ドロー', 'メイン', 'バトル', 'エンド'] as const;
export type PhaseName = typeof PHASES[number];

export interface AttributeColorConfig {
  bg: string;
  text: string;
  glow: string;
}

export const ATTRIBUTE_COLORS: Record<CardAttribute, AttributeColorConfig> = {
  '炎': { bg: 'linear-gradient(135deg, #ff4d4d 0%, #ff8533 100%)', text: '#fff', glow: '#ff6b35' },
  '水': { bg: 'linear-gradient(135deg, #4da6ff 0%, #66d9ff 100%)', text: '#fff', glow: '#4da6ff' },
  '光': { bg: 'linear-gradient(135deg, #ffd700 0%, #fff8dc 100%)', text: '#333', glow: '#ffd700' },
  '闇': { bg: 'linear-gradient(135deg, #4a0080 0%, #1a0033 100%)', text: '#e0b0ff', glow: '#9933ff' },
  '未来': { bg: 'linear-gradient(135deg, #00ffff 0%, #0080ff 100%)', text: '#fff', glow: '#00ffff' },
  '原始': { bg: 'linear-gradient(135deg, #2d5016 0%, #6b8e23 100%)', text: '#fff', glow: '#7cfc00' },
  'なし': { bg: 'linear-gradient(135deg, #808080 0%, #a9a9a9 100%)', text: '#fff', glow: '#c0c0c0' },
};

export const TYPE_ICONS: Record<CardType, string> = {
  'monster': '⚔️',
  'magic': '✨',
  'field': '🏔️',
  'phasecard': '🔮', // フェイズカード
};

// 属性アイコン画像パス
export const ATTRIBUTE_ICONS: Record<CardAttribute, string> = {
  '炎': '/images/attributes/fire.png',
  '水': '/images/attributes/water.png',
  '光': '/images/attributes/light.png',
  '闇': '/images/attributes/dark.png',
  '未来': '/images/attributes/tech.png',
  '原始': '/images/attributes/earth.png',
  'なし': '/images/attributes/normal.png',
};

// カード枠画像パス（イラストなし用 - 背景付き）
export const CARD_FRAMES_SOLID: Record<CardAttribute, string> = {
  '炎': '/images/frames/solid/fire.png',
  '水': '/images/frames/solid/water.png',
  '光': '/images/frames/solid/light.png',
  '闇': '/images/frames/solid/dark.png',
  '未来': '/images/frames/solid/tech.png',
  '原始': '/images/frames/solid/earth.png',
  'なし': '/images/frames/solid/normal.png',
};

// カード枠画像パス（イラストあり用 - 透過）
export const CARD_FRAMES_TRANSPARENT: Record<CardAttribute, string> = {
  '炎': '/images/frames/transparent/fire.png',
  '水': '/images/frames/transparent/water.png',
  '光': '/images/frames/transparent/light.png',
  '闇': '/images/frames/transparent/dark.png',
  '未来': '/images/frames/transparent/tech.png',
  '原始': '/images/frames/transparent/earth.png',
  'なし': '/images/frames/transparent/normal.png',
};

// 後方互換性のため CARD_FRAMES は SOLID を参照
export const CARD_FRAMES = CARD_FRAMES_SOLID;

// カード裏面画像パス
export const CARD_BACK = '/images/frames/back.png';

// カードイラスト画像パス
// 注意: 実際の使用時は useCardIllustration フックを使用（WebP/PNG自動検出）
export const CARD_ILLUSTRATION_BASE_PATH = '/images/cards/';
